import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Control, FieldErrors, UseFormSetError, UseFormSetValue } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";

import { FormTextInput } from "@/components/form";
import { GetAddressByCep } from "@/services/viaCep/GetCep";
import { useFormatter } from "@/utils/useFormatter";

import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type { MarketplaceAddressFormValues } from "../types";
import { BRAZIL_NO_STREET_NUMBER } from "../utils/addressHelpers";

interface MarketplaceAddressFormFieldsProps {
  control: Control<MarketplaceAddressFormValues>;
  setValue: UseFormSetValue<MarketplaceAddressFormValues>;
  setError: UseFormSetError<MarketplaceAddressFormValues>;
  errors?: FieldErrors<MarketplaceAddressFormValues>;
  showContactFields?: boolean;
  hideAddressUntilCepSubmit?: boolean;
}

function normalizeCepDigits(raw: string): string {
  return raw.replace(/\D/g, "");
}

function FormSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Stack spacing={2}>
      <Typography
        sx={{
          ...mt.sectionTitle,
          color: "primary800.main",
          fontSize: "0.9375rem",
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: alpha(theme.palette.primary800.main, 0.12),
          bgcolor: alpha(theme.palette.primary800.main, 0.02),
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}

/** Campos de endereço reutilizáveis — wizard de checkout. */
export function MarketplaceAddressFormFields({
  control,
  setValue,
  setError,
  showContactFields = true,
  hideAddressUntilCepSubmit = true,
}: MarketplaceAddressFormFieldsProps) {
  const theme = useTheme();
  const cepValue = useWatch({ control, name: "cep" });
  const streetValue = useWatch({ control, name: "street" });
  const cityValue = useWatch({ control, name: "city" });
  const neighborhoodValue = useWatch({ control, name: "neighborhood" });
  const noStreetNumber = useWatch({ control, name: "noStreetNumber" });
  const numberValue = useWatch({ control, name: "number" });

  useEffect(() => {
    if (noStreetNumber) {
      if (numberValue !== BRAZIL_NO_STREET_NUMBER) {
        setValue("number", BRAZIL_NO_STREET_NUMBER, { shouldValidate: true });
      }
      return;
    }

    if (numberValue === BRAZIL_NO_STREET_NUMBER) {
      setValue("number", "", { shouldValidate: true });
    }
  }, [noStreetNumber, numberValue, setValue]);

  const [addressExpanded, setAddressExpanded] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const cepConfirmedBySearch = useRef(false);

  useEffect(() => {
    if (!hideAddressUntilCepSubmit) {
      setAddressExpanded(true);
      return;
    }

    const digits = normalizeCepDigits(cepValue ?? "");

    if (digits.length < 8) {
      setAddressExpanded(false);
      cepConfirmedBySearch.current = false;
      return;
    }

    const hasPrefilledAddress = Boolean(
      streetValue?.trim() || cityValue?.trim() || neighborhoodValue?.trim(),
    );

    if (cepConfirmedBySearch.current || hasPrefilledAddress) {
      setAddressExpanded(true);
    }
  }, [cepValue, streetValue, cityValue, neighborhoodValue, hideAddressUntilCepSubmit]);

  const submitCepLookup = async (rawCep: string) => {
    const cep = normalizeCepDigits(rawCep);
    if (cep.length !== 8) {
      if (hideAddressUntilCepSubmit) {
        cepConfirmedBySearch.current = false;
        setAddressExpanded(false);
      }
      setError("cep", {
        type: "manual",
        message: "Informe um CEP válido com 8 dígitos.",
      });
      return;
    }

    cepConfirmedBySearch.current = true;
    if (hideAddressUntilCepSubmit) setAddressExpanded(true);

    setCepLoading(true);
    try {
      const res = await GetAddressByCep(cep);
      if (res?.erro || res?.error) {
        setError("cep", {
          type: "manual",
          message: "CEP inválido ou não encontrado.",
        });
        return;
      }

      setValue("city", res.localidade ?? "", { shouldValidate: true });
      setValue("state", res.uf ?? "", { shouldValidate: true });
      setValue("street", res.logradouro ?? "", { shouldValidate: true });
      setValue("neighborhood", res.bairro ?? "", { shouldValidate: true });
      setValue("complement", res.complemento ?? "", { shouldValidate: true });
    } finally {
      setCepLoading(false);
    }
  };

  const showAddressFields = !hideAddressUntilCepSubmit || addressExpanded;

  const fieldSx = {
    "& .MuiFilledInput-root": { backgroundColor: "neutral100.main" },
  };

  return (
    <Stack spacing={3}>
      <FormSection title="Seus dados">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormTextInput control={control} name="firstName" label="Nome" required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextInput control={control} name="lastName" label="Sobrenome" required />
          </Grid>
          {showContactFields ? (
            <>
              <Grid item xs={12} sm={6}>
                <FormTextInput
                  control={control}
                  name="cpf"
                  label="CPF"
                  required
                  mask={useFormatter.formatCPF}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextInput
                  control={control}
                  name="phone"
                  label="Telefone"
                  required
                  mask={useFormatter.formatPhoneNumber}
                />
              </Grid>
            </>
          ) : null}
        </Grid>
      </FormSection>

      <FormSection title="Local de entrega">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <Controller
              name="cep"
              control={control}
              rules={{ required: "CEP é obrigatório." }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={useFormatter.formatCEP(field.value ?? "")}
                  onChange={(event) =>
                    field.onChange(useFormatter.formatCEP(event.target.value))
                  }
                  label="CEP"
                  required
                  fullWidth
                  error={Boolean(error)}
                  helperText={error?.message ?? " "}
                  size="medium"
                  variant="filled"
                  sx={fieldSx}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void submitCepLookup(field.value);
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Buscar endereço pelo CEP"
                          title="Buscar endereço pelo CEP"
                          onClick={() => void submitCepLookup(field.value)}
                          disabled={cepLoading}
                          edge="end"
                          color="primary"
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary800.main, 0.08),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary800.main, 0.14),
                            },
                          }}
                        >
                          {cepLoading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SearchIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {hideAddressUntilCepSubmit && !showAddressFields ? (
            <Grid item xs={12}>
              <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
                Informe o CEP e clique na lupa para buscar rua, bairro e cidade.
              </Typography>
            </Grid>
          ) : null}

          {showAddressFields ? (
            <>
              <Grid item xs={12}>
                <Divider sx={{ opacity: 0.5 }} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormTextInput
                  control={control}
                  name="street"
                  label="Rua / Avenida"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextInput
                  control={control}
                  name="number"
                  label="Número"
                  required={!noStreetNumber}
                  disabled={Boolean(noStreetNumber)}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="noStreetNumber"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.value)}
                          onChange={(event) => field.onChange(event.target.checked)}
                        />
                      }
                      label="Endereço sem número (será enviado como S/N)"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormTextInput
                  control={control}
                  name="complement"
                  label="Complemento (opcional)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextInput
                  control={control}
                  name="neighborhood"
                  label="Bairro"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextInput control={control} name="city" label="Cidade" required />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormTextInput control={control} name="state" label="UF" required />
              </Grid>
            </>
          ) : null}
        </Grid>
      </FormSection>
    </Stack>
  );
}
